import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

// Utility function to merge Tailwind classes
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Format phone number for display
export function formatPhoneNumber(phone: string): string {
  // Extract country code and local number
  const match = phone.match(/^(\+\d{1,3})(\d+)$/);
  if (match) {
    const [, code, local] = match;
    return `${code} ${local}`;
  }
  return phone;
}

// Format date for display
export function formatDate(dateString: string): string {

  const date = new Date(dateString);
  // Usar Intl.DateTimeFormat para respetar la localización por defecto
  return new Intl.DateTimeFormat('es', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(date);
}

// Format date and time
export function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  // Usar la localización por defecto del entorno (no forzar 'es-CO')
  return date.toLocaleString('es', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  });
}

// Format hours for display
export function formatHours(hours: number): string {
  if (hours === 1) return '1 hora';
  return `${hours} horas`;
}

// Validate phone number (local digits without country code, 7-15 digits)
export function isValidPhone(phone: string): boolean {
  const clean = phone.replace(/\s/g, '');
  return /^[0-9]{7,15}$/.test(clean);
}

// Validate OTP code
export function isValidOTPCode(code: string): boolean {
  return /^[0-9]{6}$/.test(code);
}

// Validate email
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

// Get hours worked from hourmeter
export function getHoursWorked(hourmeter: any): number {
  return hourmeter.clientQuantity || 0;
}

// Get work date from hourmeter
export function getWorkDate(hourmeter: any): string {
  return hourmeter.startDate || hourmeter.date;
}

// Convert signature canvas to blob
export function dataURLtoBlob(dataURL: string): Blob {
  const arr = dataURL.split(',');
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
}

// Convert blob to file
export function blobToFile(blob: Blob, fileName: string): File {
  return new File([blob], fileName, { type: blob.type });
}

// Nueva función para generar nombres de grupo personalizados según el rol
// Función utilitaria para parsear la ubicación de la máquina
const parseLocationLabel = (locationString: string | null): string => {
  if (!locationString) return '';
  
  try {
    // Si es un JSON, parsearlo y extraer el label
    const locationData = JSON.parse(locationString);
    return locationData.label || locationString;
  } catch {
    // Si no es un JSON válido, devolver el string original
    return locationString;
  }
};

export const generateGroupName = (
  group: {
    costCenterId?: string;
    nameOfTheBuilding?: string | null;
    operatorName?: string | null;
    machineBrand?: string | null;
    machineModel?: string | null;
    machineCategoryName?: string | null;
    machineYear?: number | null;
    machineLocation?: string | null;
  },
  userRole?: string
): string => {
  // Para operadores: "Malta - Retroexcavadora"
  if (userRole === 'operator' || !userRole) {
    const obra = group.nameOfTheBuilding || group.costCenterId || 'Obra no definida';
    const maquina = group.machineCategoryName || 'Máquina';
    return `${obra} - ${maquina}`;
  }

  // Para residentes y supervisores: "Luis Luna - Retroexcavadora CAT - XT320 - Malta - Santa Marta, Magdalena"
  if (userRole === 'resident' || userRole === 'supervisor') {
    const operador = group.operatorName || 'Operador';
    const maquina = group.machineCategoryName || 'Máquina';
    const marca = group.machineBrand || '';
    const modelo = group.machineModel || '';
    const obra = group.nameOfTheBuilding || group.costCenterId || 'Obra';
    // Parsear la ubicación para extraer solo el label
    const ubicacion = parseLocationLabel(group.machineLocation ?? null);

    // Construir el nombre paso a paso
    let nombre = operador;
    
    // Agregar máquina y marca
    nombre += ` - ${maquina}`;
    if (marca) {
      nombre += ` ${marca}`;
    }
    
    // Agregar modelo si existe
    if (modelo) {
      nombre += ` - ${modelo}`;
    }
    
    // Agregar obra
    nombre += ` - ${obra}`;
    
    // Agregar ubicación si existe (ahora solo el label)
    if (ubicacion) {
      nombre += ` - ${ubicacion}`;
    }
    
    return nombre;
  }

  // Fallback por defecto
  return `Centro de Costo: ${group.costCenterId}${group.nameOfTheBuilding ? ` - ${group.nameOfTheBuilding}` : ''}`;
};
